/**
 * The Ankiety tab — KANON `ankiety-klienta-2026-09-16`. One range bar and a strip of charts over a
 * STREAM of every occurrence on the left and the occurrence's own report on the right.
 *
 * ── The owner moved the layout to direction A on 16 September 2026 ───────────────────────────
 * Direction B's question × occurrence matrix, its report dialog and its comparison dialog are gone.
 * What stayed is B's STYLING and its chart strip, which the owner kept by name. What replaced them
 * is `szkice/kierunek-a.html`: a list the trainer scrolls back through and a pane that answers it,
 * both on one screen, with the comparison drawn INLINE in that pane rather than over it.
 *
 * ── ONE read, and the range costs nothing ────────────────────────────────────────────────────
 * `GET /customer/{id}/surveys` takes no `from`/`to`: it answers with up to 200 occurrences and this
 * tab projects them twice — once as the stream, once as the selected occurrence's own form. So the
 * range bar re-projects an answer already in hand rather than re-fetching. The cost is stated
 * rather than hidden: a client with more than 200 occurrences is TRUNCATED at the oldest end, and
 * the tab bar's own sentence says so instead of quietly drawing a shorter history.
 *
 * ── The range narrows the CHARTS and nothing else ────────────────────────────────────────────
 * The stream is the whole history, because a list that hid the occurrence the trainer was looking
 * for because a date field said „ostatnie 8 tygodni" is a list that cannot be scrolled back
 * through. `model/surveyHistory` carries the same note where the filter used to be.
 *
 * ── The two reads and the projections over them are `lib/useSurveyHistory` ───────────────────
 * Which is also where the section's one `pending` flag is decided, and where the trap in it — a
 * DISABLED query reports `isPending` for ever — is handled.
 *
 * ── What is KEYED, and what deliberately is not ──────────────────────────────────────────────
 * The pane's CONTENT is keyed on its subject, so Motion's `swap` step cross-fades a press. Nothing
 * a trainer can focus is inside that box: the pane's head, the stream, the live region and the
 * skeleton's hand-over class all stand outside it, because a keyed box is rebuilt from scratch and
 * a control rebuilt under a finger takes the focus ring to `<body>` with it. Where a control has to
 * disappear — the tick after a mark, the ✕ after a close — focus is moved to the stream row the
 * trainer came from, deliberately and by hand.
 *
 * The whole stateful body is keyed on `clientId`, which is what „Następna ›" needs: the selection,
 * the chip, the comparison and the strip's form are all statements about ONE client, and carrying
 * them to the next one opened her tab on a row that is not hers.
 *
 * ── The signed media URLs expire, and the tab re-reads rather than refreshing one of them ────
 * `photoUrls` are signed for about twenty minutes. Choosing an occurrence whose signature has
 * lapsed invalidates the whole survey read, because the contract publishes the table per assignment
 * and there is no per-media refresh to call.
 */

import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SurveyReport } from '~/features/survey-report';
import { useMarkSurveyReviewed } from '~/features/survey-review';
import { screenMotion } from '~/shared/design-system/motion';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { clientSurveysQueryKey } from '../api/clientDetailQueries';
import { useEnrolledSurveys } from '../lib/useEnrolledSurveys';
import { useSurveyHistory } from '../lib/useSurveyHistory';
import { surveysEmptyKind } from '../model/enrolledSurveys';
import { describeSurveyPane } from '../model/surveyPane';
import { decideSurveysRange, defaultSurveysRange } from '../model/surveyRange';
import { useCardAnnounce } from './cardAnnouncer';
import { DateRangeBar } from './DateRangeBar';
import { EnrolledSurveysBar } from './EnrolledSurveysBar';
import detail from './DetailTab.module.css';
import { usePublishTabMeta } from './detailTabMeta';
import { SurveyChartsStrip } from './SurveyChartsStrip';
import { SurveyCompare } from './SurveyCompare';
import pane from './SurveyPane.module.css';
import { SurveyPaneHead } from './SurveyPaneHead';
import { SurveyStream } from './SurveyStream';
import styles from './SurveysTab.module.css';

/** Two occurrences compared, and no more. A third check retires the older one. */
const COMPARE_LIMIT = 2;

/** The pane's wait, in the shape of a report: a head, then answer blocks. */
const SKELETON_BLOCKS = [0, 1, 2, 3] as const;

/**
 * What the live region says next.
 *
 * `subject` is "whatever the pane is now about" — `describeSurveyPane` has already written that
 * sentence for the head, and the region publishes the same one rather than a second construction of
 * it. A `said` carries its own words for the one act whose sentence is not a description of a
 * state: „Oznaczono tydz. 8 jako przejrzaną" is about something that HAPPENED.
 *
 * It is set by the handlers and read in an effect, because a handler runs BEFORE the render that
 * knows what it changed — announcing `head.announcement` from inside a press published the sentence
 * the pane was leaving.
 */
type Saying = { readonly kind: 'subject' } | { readonly kind: 'said'; readonly text: string };

/** One object, so pressing the same row twice is one change and not two announcements. */
const SUBJECT: Saying = { kind: 'subject' };

interface SurveysTabProps {
  readonly clientId: number;
  /**
   * The occurrence to open on — a survey task's „Przejrzyj" (`?survey=`). `null` lets the model open
   * on the newest answered one.
   */
  readonly openOccurrenceId: number | null;
  readonly reducedMotion: boolean;
  /**
   * Open „Przypisz ankietę" (8 October 2026), handing over the control that asked. `null` until the
   * card's header read has named the client — the dialog is built for a named one.
   */
  readonly onAssignSurvey: ((opener: HTMLElement) => void) | null;
}

/**
 * Keyed on the client, so every piece of state below is discarded when „Następna ›" moves to the
 * next one. A reset written by hand would be four setters somebody has to remember to extend.
 */
export function SurveysTab({
  clientId,
  openOccurrenceId,
  reducedMotion,
  onAssignSurvey,
}: SurveysTabProps) {
  return (
    <SurveysTabBody
      key={clientId}
      clientId={clientId}
      openOccurrenceId={openOccurrenceId}
      reducedMotion={reducedMotion}
      onAssignSurvey={onAssignSurvey}
    />
  );
}

/**
 * „Przypisz ankietę", drawn from the first frame so its arrival moves nothing; refused for the moment
 * the card's header read is still open.
 */
function AssignSurveyButton({
  onAssignSurvey,
  className,
}: {
  readonly onAssignSurvey: SurveysTabProps['onAssignSurvey'];
  readonly className: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`${String(ghost.ghost)} ${className}`}
      aria-disabled={onAssignSurvey === null ? true : undefined}
      onClick={(event) => {
        onAssignSurvey?.(event.currentTarget);
      }}
    >
      {t('web:clientDetail.surveys.assign')}
    </button>
  );
}

function SurveysTabBody({
  clientId,
  openOccurrenceId,
  reducedMotion,
  onAssignSurvey,
}: SurveysTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [range, setRange] = useState(() => defaultSurveysRange(new Date()));
  const [chipId, setChipId] = useState<string | null>(null);
  /** The occurrence the pane is showing. `null` lets the model open on the newest answered one. */
  const [selectedId, setSelectedId] = useState<number | null>(openOccurrenceId);
  /**
   * The last occurrence of a CADENCED form the trainer opened — where the chart strip stays when
   * they go and read a one-off form. `null` until they move it. See `chartStripOccurrenceId`.
   */
  const [lastCadencedId, setLastCadencedId] = useState<number | null>(null);
  /**
   * The occurrences checked for a comparison, or `null` when the trainer is not comparing.
   *
   * An empty array is a real state and is not `null`: compare mode with the first pick cleared is
   * a stream full of checkboxes and no anchor yet. Leaving the mode clears the checks with it,
   * because a check that survived the close would decide the next comparison silently.
   */
  const [comparing, setComparing] = useState<readonly number[] | null>(null);
  /** The stream's own card, so a control that disappears can hand focus back to a row inside it. */
  const streamRef = useRef<HTMLElement>(null);
  const announce = useCardAnnounce();
  /** `null` until the trainer's first act: a region that spoke on arrival would read the tab out. */
  const [saying, setSaying] = useState<Saying | null>(null);

  // The ceiling, "no range may end in the future" and the four field bounds are Trendy's own module
  // and deliberately so — a second copy of a bound the backend owns would disagree the first time
  // one of them moved. The FLOOR is this tab's: Trendy's seven days exist because `/muscle-volume`
  // divides by the window, and this read divides by nothing. See `model/surveyRange`.
  const bounds = decideSurveysRange(range, { t, today: new Date() });

  const { view, strip, stream, pending, failed } = useSurveyHistory(clientId, {
    chartRange: range,
    occurrenceId: selectedId,
    chipId,
    lastCadencedId,
  });
  const enrolled = useEnrolledSurveys(clientId);
  // ONE gate over both reads: the empty pane's sentence depends on what is assigned, and a pane
  // that said „nie ma ankiet" and then „czeka na odpowiedzi" would be two answers in a row.
  const gate = useSkeletonGate(pending || enrolled.pending);
  const emptyKind = surveysEmptyKind(enrolled.enrolled);
  const review = useMarkSurveyReviewed({ invalidates: [clientSurveysQueryKey(clientId)] });
  const head = describeSurveyPane(view, { t, comparing });

  useEffect(() => {
    if (saying === null) return;
    announce(saying.kind === 'subject' ? head.announcement : saying.text);
  }, [saying, head.announcement, announce]);

  /* The tab bar's right-hand line stays EMPTY on this tab (owner, 16 September 2026): the stream's
   * head already counts what waits, and a sentence repeating it up there was noise. Publishing `null`
   * clears whatever the previous tab left. */
  usePublishTabMeta(null);

  /**
   * An occurrence is about to draw a signed URL. If the signature has already lapsed, the whole read
   * is re-fetched — the contract publishes the table per assignment and has no per-media refresh.
   *
   * `useCallback` is here for CORRECTNESS, not for memoisation: it reads the clock, and the purity
   * lint refuses a clock read in a function the compiler cannot see is deferred. Wrapping it is what
   * says "this runs on a press", and the press is the only moment the answer means anything.
   */
  const refreshExpiredMedia = useCallback(
    (expiry: string | null) => {
      if (expiry !== null && Date.parse(expiry) <= Date.now()) {
        void queryClient.invalidateQueries({ queryKey: clientSurveysQueryKey(clientId) });
      }
    },
    [clientId, queryClient],
  );

  /**
   * Put the focus ring back on a row of the stream.
   *
   * SYNCHRONOUS, and it can be: the row is on screen before the press and still on screen after it
   * — only its role changes — so React reconciles the same element and the focus set here survives
   * the re-render. What must not happen is the alternative the tab had, where the control that was
   * focused vanished with the keyed box and nothing took its place.
   */
  const focusRow = (id: number) => {
    streamRef.current?.querySelector<HTMLElement>(`[data-survey-row="${String(id)}"]`)?.focus();
  };

  const select = (id: number) => {
    refreshExpiredMedia(view.photoUrlsExpireAt);
    setSelectedId(id);
    setSaying(SUBJECT);
    // The strip follows a form only while that form has a cadence — see `chartStripOccurrenceId`.
    // A one-off form leaves the last cadenced one where it is.
    if (stream.index.get(id)?.cadenced === true) setLastCadencedId(id);
  };

  /** The pair, oldest first, so the change column reads left to right in time. */
  const orderedPair = (ids: readonly number[]): readonly number[] =>
    // Ordered by the MODEL's own position in the whole history, never by an index into the rows on
    // screen: those are the chip's, and an occurrence the chip filtered out has no index at all —
    // `findIndex` answered `-1` for it and the pair came back the wrong way round.
    [...ids].sort(
      (left, right) => (stream.index.get(left)?.order ?? 0) - (stream.index.get(right)?.order ?? 0),
    );

  const toggleCompare = (id: number) => {
    if (comparing === null) return;
    setSaying(SUBJECT);
    if (comparing.includes(id)) {
      setComparing(comparing.filter((entry) => entry !== id));
      return;
    }
    // A third check retires the oldest rather than refusing the press: the row would otherwise
    // become a dead control the moment two were chosen.
    const next = [...comparing, id].slice(-COMPARE_LIMIT);
    setComparing(next.length === COMPARE_LIMIT ? orderedPair(next) : next);
    // Closing the comparison returns the pane to the row the trainer touched last.
    select(id);
  };

  /** What the pane is about, as one key — a change to it is what the swap animation is FOR. */
  const paneKey =
    head.pair === null
      ? `report-${String(view.selected?.id ?? 0)}`
      : `compare-${String(head.pair.left.id)}-${String(head.pair.right.id)}`;

  /** Leave compare mode, and hand focus back to the row the pane is about. */
  const closeCompare = () => {
    setComparing(null);
    setSaying(SUBJECT);
    const current = view.selected?.id;
    if (current !== undefined) focusRow(current);
  };

  return (
    <div className={detail.tab}>
      <DateRangeBar
        range={range}
        bounds={bounds}
        onChange={setRange}
        /* A reserved BLANK while the read is open, and after one that did not answer. „0 wysyłek w
         * zakresie" during the wait is a count of an answer nobody has yet — replaced by a
         * different number a moment later — and after a failure it is a count of nothing that was
         * read, stated as a fact about the client. */
        note={
          // The GATE's wait: printed on the read, the count would appear while the tab is still
          // drawing bars, which is a change inside the wait rather than the answer arriving.
          gate.waiting || failed
            ? ''
            : t('web:clientDetail.surveys.rangeNote', { count: view.columns.length })
        }
        entrance={screenMotion('rise', { reducedMotion, index: 0 })}
      />

      <EnrolledSurveysBar
        state={enrolled}
        gate={gate}
        onAssignSurvey={onAssignSurvey}
        entrance={screenMotion('rise', { reducedMotion, index: 1 })}
      />

      {/* A failed read keeps every box. The strip draws its notice inside the reserved plot and the
        * pane an `EmptyState` with the retry on it — a bare line where a chart strip, a list and a
        * report were about to be collapses the tab by several hundred pixels, which is the jump the
        * whole loading policy is written against. */}
      <motion.div {...screenMotion('rise', { reducedMotion, index: 2 })}>
        <SurveyChartsStrip
          series={strip.series}
          xDomain={strip.xDomain}
          gate={gate}
          failed={failed}
          /* Nothing to say rather than „Brak pomiarów w tym zakresie": a client with no surveys at
           * all is not a window with nothing in it, and the pane says the one sentence there is. */
          silent={failed || stream.empty}
          reducedMotion={reducedMotion}
        />
      </motion.div>

      <div className={styles.split}>
        <motion.div
          className={styles.streamColumn}
          {...screenMotion('rise', { reducedMotion, index: 3 })}
        >
          <SurveyStream
            ref={streamRef}
            stream={stream}
            currentId={view.selected?.id ?? null}
            truncated={view.isTruncated}
            comparing={comparing}
            modeNote={head.modeNote}
            onSelect={select}
            onToggleCompare={toggleCompare}
            onEnterCompare={() => {
              // Pre-checked with the occurrence the pane is showing, so the comparison is anchored
              // to a FORM before the first press and every other form's rows can say why they are
              // refused. Nothing on screen to anchor to is an empty mode, not a refused one.
              setComparing(view.selected === null ? [] : [view.selected.id]);
              setSaying(SUBJECT);
            }}
            onExitCompare={closeCompare}
            onChip={(id) => {
              setChipId(id);
            }}
            gate={gate}
            failed={failed}
          />
        </motion.div>

        <motion.section
          /* The reserve covers the WAIT and the FAILURE: the box the card held while the read was
           * open must not collapse under the answer, and „nie udało się" is an answer. */
          /* The hand-over class came off this element on 21 September 2026: it carried the
           * entrance AND the hand-over, two gestures on one box. The reveal belongs on what
           * actually REPLACES the bars, which is the head and the two `EmptyState`s below. */
          className={`${String(detail.card)} ${String(pane.pane)} ${gate.waiting || failed ? String(pane.paneReserve) : ''}`}
          /* NAMED, so the tab is a master and a detail rather than one undivided panel: a screen
           * reader moving by region lands on the list and on the answer to it, and the name is
           * static because it says which HALF of the tab this is, not what is in it. */
          aria-label={t('web:clientDetail.surveys.paneLabel')}
          /* The WAIT's, not the read's (owner, 21 September 2026): while the gate holds the bars
           * this pane is still about to change, and `false` during that tail would report a
           * settlement that has not happened. It is the same value as the branch below and the
           * hidden sentence inside it. */
          aria-busy={gate.waiting}
          {...screenMotion('rise', { reducedMotion, index: 4 })}
        >
          {/* The WAIT first, and the failure inside the not-waiting arm. A failure tested before
            * the gate drew „nie udało się" while `aria-busy` above still said the pane was
            * changing, and a retry's bars would have had nowhere to appear. */}
          {gate.waiting ? (
            <>
              <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
              {/* A report's shape is a head over blocks, so its skeleton is bars where those will
                * be — inside the card's own reserve, so drawing them moves nothing. */}
              {gate.skeleton ? (
                <span className={pane.skelPane}>
                  <i className={`${String(skel.bar)} ${String(pane.skelHead)}`} />
                  {SKELETON_BLOCKS.map((block) => (
                    <i key={block} className={`${String(skel.bar)} ${String(pane.skelBlock)}`} />
                  ))}
                </span>
              ) : null}
            </>
          ) : failed ? (
            /* NEUTRAL: the read did not answer. It is a statement about this attempt, and the
             * trainer's move is to ask again — which is the control inside the sentence, because
             * a dead end with no way out of it is the reason this state was three sentences and no
             * act before. */
            <EmptyState
              className={gate.reveal}
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={
                <span>
                  {t('web:clientDetail.surveys.failed')}{' '}
                  <button
                    type="button"
                    className={inlineButton.inlineTextButton}
                    onClick={() => {
                      void queryClient.invalidateQueries({
                        queryKey: clientSurveysQueryKey(clientId),
                      });
                    }}
                  >
                    {t('web:clientDetail.states.retry')}
                  </button>
                </span>
              }
            />
          ) : (view.empty || view.selected === null) && emptyKind === 'awaiting' ? (
            /* POSITIVE: something is assigned and its first cycle has not been answered yet — the
             * history gains a row only when the backend sends one. */
            <EmptyState
              className={gate.reveal}
              mark={<InboxMarkIcon />}
              tone="positive"
              title={t('web:clientDetail.surveys.awaitingTitle')}
              hint={t('web:clientDetail.surveys.awaitingHint')}
            />
          ) : view.empty || view.selected === null ? (
            /* ACTION: a client with no survey at all is a thing the trainer can fix — here, since
             * 8 October 2026, and the act stands under the sentence that names it. */
            <div className={`${String(styles.emptyPane)} ${gate.reveal}`}>
              <EmptyState
                mark={<InboxMarkIcon />}
                tone="action"
                title={t('web:clientDetail.surveys.emptyTitle')}
                hint={t('web:clientDetail.surveys.emptyHint')}
              />
              <AssignSurveyButton
                onAssignSurvey={onAssignSurvey}
                className={String(styles.assignEmpty)}
              />
            </div>
          ) : (
            <>
              {/* OUTSIDE the keyed box, and that is the whole point — see the header. */}
              <SurveyPaneHead
                className={gate.reveal}
                head={head}
                column={head.pair === null ? view.selected : null}
                onMarkReviewed={(columnId) => {
                  const at = view.selected?.ordinalLabel ?? '';
                  review.markReviewed(columnId, () => {
                    setSaying({
                      kind: 'said',
                      text: t('web:clientDetail.surveys.markedReviewed', { at }),
                    });
                    focusRow(columnId);
                  });
                }}
                marking={review.marking}
                markFailed={review.markFailed}
                onSwap={() => {
                  setComparing((current) => (current === null ? null : [...current].reverse()));
                  // Focus stays where it is: „zamień strony" is still there, with the two ordinals
                  // in its head the other way round. The live region says which way round.
                  setSaying(SUBJECT);
                }}
                onClose={closeCompare}
              />

              {/* NO hand-over class here, deliberately. This box is KEYED on its subject, so it
                * is rebuilt on every press and would replay the 120ms reveal each time — and
                * because a CSS animation outranks the inline opacity Motion writes, the replay
                * would fight `swap`'s own cross-fade for its first frames. It does not need one
                * either: it mounts with the answer and `swap` IS its entrance. The hand-over is
                * on the head above and on the two `EmptyState`s, which have no gesture of their
                * own. */}
              <motion.div key={paneKey} {...screenMotion('swap', { reducedMotion, index: 0 })}>
                {head.pair === null ? (
                  <SurveyReport view={view} />
                ) : (
                  <SurveyCompare
                    view={view}
                    leftId={head.pair.left.id}
                    rightId={head.pair.right.id}
                    caption={head.title}
                  />
                )}
              </motion.div>
            </>
          )}
        </motion.section>
      </div>
    </div>
  );
}
